/*
 * <ugc-video> — vertical UGC video gallery.
 * - Muted autoplay previews while cards are in view (respects reduced-motion).
 * - Arrow buttons scroll the carousel; native swipe on touch.
 * - Tap a card → fullscreen stories-style player with sound + prev/next.
 */
if (!customElements.get('ugc-video')) {
  customElements.define(
    'ugc-video',
    class UgcVideo extends HTMLElement {
      constructor() {
        super();
        this.autoplay = this.dataset.autoplay === 'true';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.track = this.querySelector('.ugc__track');
        this.cards = Array.from(this.querySelectorAll('[data-ugc-open]'));
        this.previews = Array.from(this.querySelectorAll('[data-ugc-video]'));

        this.modal = this.querySelector('[data-ugc-modal]');
        this.modalVideo = this.querySelector('[data-ugc-modal-video]');
        this.modalName = this.querySelector('[data-ugc-modal-name]');
        this.modalCaption = this.querySelector('[data-ugc-modal-caption]');
        this.modalRating = this.querySelector('[data-ugc-modal-rating]');

        this.index = 0;
      }

      connectedCallback() {
        // Open fullscreen on card tap.
        this.cards.forEach((card) =>
          card.addEventListener('click', () => this.open(parseInt(card.dataset.index, 10) || 0))
        );

        // Arrow scroll.
        this.navButtons = Array.from(this.querySelectorAll('[data-ugc-scroll]'));
        this.navButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.scrollByCards(parseInt(btn.dataset.ugcScroll, 10)))
        );

        // Hide arrows when the track doesn't overflow; disable at the ends.
        this._updateNav = this.updateNav.bind(this);
        this.track.addEventListener('scroll', this._updateNav, { passive: true });
        window.addEventListener('resize', this._updateNav);
        window.addEventListener('load', this._updateNav);
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver(() => this.updateNav());
          this._ro.observe(this.track);
        }
        this.updateNav();
        setTimeout(this._updateNav, 300);

        // Modal controls.
        this.querySelector('[data-ugc-close]')?.addEventListener('click', () => this.close());
        this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.step(1));
        this.modal?.addEventListener('click', (e) => {
          if (e.target === this.modal) this.close();
        });
        document.addEventListener('keydown', (e) => {
          if (this.modal.hidden) return;
          if (e.key === 'Escape') this.close();
          if (e.key === 'ArrowRight') this.step(1);
          if (e.key === 'ArrowLeft') this.step(-1);
        });

        // Basic swipe on the modal stage.
        let x0 = null;
        const stage = this.querySelector('.ugc__stage');
        stage?.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
        stage?.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 45) this.step(dx < 0 ? 1 : -1);
          x0 = null;
        });

        this.setupPreviews();
      }

      /* Muted autoplay of previews while in view. */
      setupPreviews() {
        if (!this.autoplay || this.reduceMotion || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const vid = entry.target;
              if (this.modalOpen) return;
              if (entry.isIntersecting) {
                vid.play().catch(() => {});
              } else {
                vid.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        this.previews.forEach((v) => this.observer.observe(v));
      }

      pausePreviews() {
        this.previews.forEach((v) => v.pause());
      }

      scrollByCards(dir) {
        const card = this.cards[0];
        const step = card ? card.getBoundingClientRect().width + 16 : 240;
        this.track.scrollBy({ left: step * dir, behavior: 'smooth' });
      }

      /* Hide the arrows entirely when there's nothing to scroll. */
      updateNav() {
        if (!this.track || !this.navButtons || this.navButtons.length === 0) return;
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        const atStart = this.track.scrollLeft <= 2;
        const atEnd =
          this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
        this.navButtons.forEach((btn) => {
          const dir = parseInt(btn.dataset.ugcScroll, 10);
          btn.hidden = !scrollable;
          btn.disabled = (dir < 0 && atStart) || (dir > 0 && atEnd);
        });
      }

      open(index) {
        this.modalOpen = true;
        this.pausePreviews();
        this.load(index);
        this.modal.hidden = false;
        requestAnimationFrame(() => this.modal.classList.add('is-open'));
        document.body.style.overflow = 'hidden';
      }

      close() {
        this.modalOpen = false;
        this.modal.classList.remove('is-open');
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.innerHTML = '';
        this.modal.hidden = true;
        document.body.style.overflow = '';
      }

      step(dir) {
        const next = (this.index + dir + this.cards.length) % this.cards.length;
        this.load(next);
      }

      load(index) {
        this.index = index;
        const card = this.cards[index];
        if (!card) return;

        // Copy the <source> list from the card's preview video.
        const sources = card.querySelectorAll('[data-ugc-video] source');
        this.modalVideo.innerHTML = '';
        sources.forEach((s) => {
          const el = document.createElement('source');
          el.src = s.src;
          el.type = s.type;
          this.modalVideo.appendChild(el);
        });
        this.modalVideo.muted = false;
        this.modalVideo.load();
        this.modalVideo.play().catch(() => {});

        // Meta.
        if (this.modalName) this.modalName.textContent = card.dataset.name || '';
        if (this.modalCaption) this.modalCaption.textContent = card.dataset.caption || '';
        if (this.modalRating) {
          const r = parseInt(card.dataset.rating, 10) || 0;
          this.modalRating.textContent = r > 0 ? '★★★★★☆☆☆☆☆'.slice(5 - r, 10 - r) : '';
        }
      }
    }
  );
}
